import React from 'react';
import ReactDOMFactories from 'react-dom-factories';

import { pageContext } from 'ctm-util-functions';
import * as ctmComponents from 'ctm-components';

/**
 * @desc Factory to create CTM components or generic html tags from schema
 */
class ComponentFactory {

    /**
     * @desc Initialize the ComponentFactory of the given schema model
     * @constructor
     */
    constructor() {
        this.props = pageContext.getProps();
    }

    /**
     * @desc Main method to be used to create ctm component of the given schema
     * @param {Object} componentSchema - schema of individual component, may or may not have children
     * @param {Object} extraProps - extraProps you want to pass down to all sub components
     * @param {Array|Node|null} children - An array of children components
     * @returns {Node} react component with props and children filled
     */
    createComponent = (componentSchema, extraProps, children) => {
        // eslint-disable-next-line prefer-const
        let { componentName, ...props } = componentSchema;
        if (componentName.includes('_')) {
            // For the cases where we've wrapped a form as a component
            // Calling back out to the director feels a bit wrong, but is kinda necessary due to the wrapping fo the form in a component.
            // Putting this logic in the director means that we would need direct access to the componentSchema to map the component
            // layout mapping name to the actual componentName - eg we have access to the name: 'component_callMeBackColumnForm'
            // but we need the componentSchema to know that this maps to the 'form_callMeBack' component.
            // This felt better than adding component specific logic to the director
            return this.props.director.constructComponents([componentName], { ...props, ...extraProps })[0];
        }

        const component = this._resolveComponent(componentName);

        const componentExtraProps = {};
        if (extraProps) {
            // @todo clean this up under EVT-1676
            for (const key of Object.keys(extraProps)) {
                // if a parent has defined extraProps for this componentKey, add them to the components extraProps
                // Use Case: Generic SpeechBubble component with text defined in the Cross Sell component
                if (key === componentSchema.componentKey) {
                    Object.assign(componentExtraProps, extraProps[key]);
                } else if (key.endsWith('Props') && !Object.keys(props).includes(key)) {
                    // If a props namespace has been defined on the parent and the child doesn't include it, add it to the child's
                    // extraProps
                    componentExtraProps[key] = extraProps[key];
                } else if (!key.endsWith('Props')) {
                    // this is mainly for componentKey - see EVT-1676
                    componentExtraProps[key] = extraProps[key];
                }
            }
        }

        // @todo: REMOVE ONCE ALL COMPONENT SCHEMA THAT RENDERS BELOW COMPONENTS HAS REMOVED SHAPED PROPS SUCH AS visualProps
        const shapedPropsComponentList = [
            'Flexbox', 'SimpleFlexbox', 'Anchor', 'Button',
            'Icon', 'InternalLink', 'Tooltip', 'Text', 'TextTemplate'
        ];
        if (shapedPropsComponentList.includes(componentName)) {
            // eslint-disable-next-line react/prop-types
            const { visualProps = {}, elementProps = {}, contentProps = {}, eventProps = {}, ...rest } = props;
            props = { ...visualProps, ...elementProps, ...contentProps, ...eventProps, ...rest };
        }

        return React.createElement(
            component,
            {
                key: componentSchema.componentKey || componentName,
                ...props,
                ...componentExtraProps
            },
            children
        );
    };

    /**
     * @desc Private method to check the component name to determine if it is a ctm component, vertical specific component or generic html tag
     * @param {String} componentName - tag name of a component, e.g. 'Logo' or generic html tag like 'div'.
     * @returns {String|Object} - ReactClass type, or a html tag string
     * @throws {Error} Will throw an error if a componentName is not a ctm components or a valid dom element defined in ReactDOMFactories
     * @private
     */
    _resolveComponent = (componentName) => {
        let component = null;

        if (ctmComponents[componentName]) {
            component = ctmComponents[componentName];
        } else if (this.props.verticalComponents[componentName]) {
            component = this.props.verticalComponents[componentName];
        } else if (ReactDOMFactories.hasOwnProperty(componentName)) {
            component = componentName;
        } else {
            throw Error(`ComponentFactory could not resolve component ${componentName} as it is missing from ctm-components`);
        }

        return component;
    };

}

export { ComponentFactory };
